import { useEffect, useRef, useState } from 'react';
import './index.css';

// Preview data only; WordPress passes the real showcase from render_special_deals_hero().
const demoShowcase = {
  best: '$350',
  initial: 4,
  zones: [
    { amount: '$50', rule: 'AMD builds', from: 0, to: 23.08, tier: 50 },
    { amount: '$150', rule: 'From $3,000', from: 23.08, to: 53.85, tier: 150 },
    { amount: '$350', rule: 'From $5,000', from: 53.85, to: 100, tier: 350 },
  ],
  pcs: [
    { name: 'ASUS Prime AP202 White', specs: 'Radeon RX 9060 XT 8GB, Ryzen 5 9600X', url: '#', image: '/assets/custom-pc/prime-ap202.webp', thumb: '/assets/custom-pc/prime-ap202.webp', price: '$2,066', saving: '$50', pay: '$2,016', pos: 8.71, tier: 50 },
    { name: 'MAG PANO 120R PZ', specs: 'Radeon RX 9070 XT 16GB, Ryzen 7 9800X3D', url: '#', image: '/assets/custom-pc/td500-white.webp', thumb: '/assets/custom-pc/td500-white.webp', price: '$2,999', saving: '$50', pay: '$2,949', pos: 23.06, tier: 50 },
    { name: 'HYTE X50 Purple', specs: 'Radeon RX 7900 XT 20GB, Ryzen 9 9900X', url: '#', image: '/assets/custom-pc/hyte-x50.webp', thumb: '/assets/custom-pc/hyte-x50.webp', price: '$3,429', saving: '$150', pay: '$3,279', pos: 29.68, tier: 150 },
    { name: 'TUF Gaming GT501 White', specs: 'GeForce RTX 5080 16GB, Intel Ultra 9 285K', url: '#', image: '/assets/custom-pc/td500-white.webp', thumb: '/assets/custom-pc/td500-white.webp', price: '$4,228', saving: '$150', pay: '$4,078', pos: 41.97, tier: 150 },
    { name: 'ROG Hyperion GR701', specs: 'GeForce RTX 5090 32GB, Ryzen 9 9950X3D', url: '#', image: '/assets/custom-pc/hyte-x50.webp', thumb: '/assets/custom-pc/hyte-x50.webp', price: '$5,640', saving: '$350', pay: '$5,290', pos: 63.69, tier: 350 },
  ],
};

export function Arrow({ direction = 'right' }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d={direction === 'down' ? 'M12 4v15m-6-6 6 6 6-6' : 'M4 12h15m-6-6 6 6-6 6'} /></svg>;
}

const ROTATE_MS = 5000;

export default function SpecialDealsHero({ showcase = demoShowcase }) {
  const { pcs, zones } = showcase;
  const [active, setActive] = useState(showcase.initial);
  const [playing, setPlaying] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(true);
  const [reduced, setReduced] = useState(false);
  const section = useRef(null);
  const pc = pcs[active];

  useEffect(() => {
    const media = matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => { setReduced(media.matches); if (media.matches) setPlaying(false); };
    setReduced(media.matches); setPlaying(!media.matches);
    media.addEventListener('change', sync);
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: .25 });
    observer.observe(section.current);
    const onVisibility = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => { observer.disconnect(); media.removeEventListener('change', sync); document.removeEventListener('visibilitychange', onVisibility); };
  }, []);

  useEffect(() => {
    if (!playing || hovered || focused || !visible || reduced || pcs.length < 2) return;
    const timer = setInterval(() => setActive(index => (index + 1) % pcs.length), ROTATE_MS);
    return () => clearInterval(timer);
  }, [playing, hovered, focused, visible, reduced, pcs.length]);

  function select(index) {
    setPlaying(false);
    setActive(index);
  }

  function onMarkerKey(event) {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const next = (active + step + pcs.length) % pcs.length;
    select(next);
    event.currentTarget.parentElement.children[next].focus();
  }

  return <section ref={section} className="sdh-hero" aria-label="Special deals">
    <div className="sdh-inner">
      <div className="sdh-copy">
        <p className="sdh-eyebrow">SPECIAL DEALS</p>
        <h1 className="sdh-title">Save up to <span>{showcase.best}</span>.<br />No code needed.</h1>
        <p className="sdh-description">Every PC that isn&rsquo;t on sale gets the biggest saving it qualifies for, taken off automatically at checkout. PCs on sale keep their sale price instead.</p>
        <div className="sdh-actions">
          <a className="sdh-primary" href="#todays-deals">Shop PCs on sale <Arrow direction="down" /></a>
          <a className="sdh-secondary" href="/custom-pc-builder/">Build a custom PC <span aria-hidden="true">→</span></a>
        </div>
      </div>
      <div className="sdh-stage" role="region" aria-roledescription="carousel" aria-label="Automatic savings by PC price" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onFocusCapture={() => setFocused(true)} onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
        <div className="sdh-feature">
          <div className="sdh-photos">
            {pcs.map((item, index) => <img key={item.url} className={`sdh-photo${index === active ? ' sdh-photo-active' : ''}`} src={item.image} alt="" width="640" height="720" loading={index === showcase.initial ? 'eager' : 'lazy'} fetchPriority={index === showcase.initial ? 'high' : 'auto'} draggable="false" />)}
          </div>
          <div className="sdh-readout" aria-live={playing ? 'off' : 'polite'}>
            <p className="sdh-pc-name">{pc.name}</p>
            <p className="sdh-pc-specs">{pc.specs}</p>
            <dl className="sdh-math">
              <div><dt>Price</dt><dd>{pc.price}</dd></div>
              <div className="sdh-math-saving"><dt>Off at checkout</dt><dd>&minus;{pc.saving}</dd></div>
              <div className="sdh-math-total"><dt>You pay</dt><dd>{pc.pay}</dd></div>
            </dl>
            <div className="sdh-pc-actions">
              <a className="sdh-pc-link" href={pc.url}>View this PC <Arrow /></a>
              <button type="button" className="sdh-pause" aria-label={playing ? 'Pause automatic rotation' : 'Start automatic rotation'} onClick={() => setPlaying(value => !value)} disabled={reduced || pcs.length < 2}>{playing ? 'Pause' : 'Play'}<span aria-hidden="true">{playing ? 'Ⅱ' : '▷'}</span></button>
            </div>
          </div>
        </div>
        <div className="sdh-map">
          <div className="sdh-markers">
            {pcs.map((item, index) => <button key={item.url} type="button" className={`sdh-marker${index === active ? ' sdh-marker-active' : ''}`} style={{ left: `${item.pos}%` }} aria-label={`${item.name}, ${item.price}`} aria-pressed={index === active} tabIndex={index === active ? 0 : -1} onClick={() => select(index)} onKeyDown={onMarkerKey}><img src={item.thumb} alt="" width="150" height="150" loading="lazy" draggable="false" /></button>)}
          </div>
          <div className="sdh-track">
            {zones.map(zone => <span key={zone.tier} className={`sdh-band${zone.tier === pc.tier ? ' sdh-band-active' : ''}`} style={{ left: `${zone.from}%`, width: `${zone.to - zone.from}%` }} />)}
          </div>
          <div className="sdh-zones">
            {zones.map(zone => <p key={zone.tier} className={`sdh-zone${zone.tier === pc.tier ? ' sdh-zone-active' : ''}`} style={{ left: `${zone.from}%`, width: `${zone.to - zone.from}%` }}><span className="sdh-zone-amount">{zone.amount} off</span><span className="sdh-zone-rule">{zone.rule}</span></p>)}
          </div>
        </div>
      </div>
    </div>
  </section>;
}
